import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

import API from '@/config/api';
// API imported from config;

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const { user, token, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  const fetchSubscription = useCallback(async () => {
    if (!user || !token || user.role === 'parent' || user.role === 'staff' || user.role === 'driver') {
      // Parents, staff, and drivers don't need subscription
      setHasAccess(user?.role === 'parent' || user?.role === 'staff' || user?.role === 'driver');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Subscription API response:', res.data);
      setSubscription(res.data.subscription || null);
      
      // Check if user has access — active OR currently on a 7-day trial.
      // Stripe reports status="trialing" while the trial is live, then flips to
      // "active" once it charges the card on day 8.
      const status = res.data.subscription?.status;
      const isActive = res.data.has_subscription &&
                       (status === 'active' || status === 'trialing') &&
                       res.data.subscription?.stripe_subscription_id;
      
      // Also check if terms were accepted
      const termsAccepted = res.data.terms_accepted || false;
      
      console.log('Access check:', { isActive, status, termsAccepted, hasAccess: isActive && termsAccepted });
      setHasAccess(isActive && termsAccepted);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    // Wait for auth to finish loading before checking subscription
    if (authLoading) {
      setLoading(true);
      return;
    }
    
    if (user && token) {
      setLoading(true);
      fetchSubscription();
    } else {
      // No user, no subscription needed
      setSubscription(null);
      setHasAccess(false);
      setLoading(false);
    }
  }, [user, token, authLoading, fetchSubscription]);

  const refreshSubscription = useCallback(async () => {
    console.log('[SubscriptionContext] refreshSubscription called');
    setLoading(true);
    await fetchSubscription();
    console.log('[SubscriptionContext] refreshSubscription completed');
    // Return the current hasAccess state after fetch
    // Note: Due to React's async state updates, we need to re-check
    return new Promise((resolve) => {
      // Small delay to ensure state has propagated
      setTimeout(() => {
        resolve(hasAccess);
      }, 100);
    });
  }, [fetchSubscription, hasAccess]);

  return (
    <SubscriptionContext.Provider value={{ 
      subscription, 
      loading: loading || authLoading, // Include auth loading state 
      hasAccess, 
      refreshSubscription,
      requiresSubscription: user && (user.role === 'admin' || user.role === 'provider')
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
